import { USStateNames, USStateOptions, USStates } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { ActionButtonPageTitle, Button } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { TextInputFormik } from '@strata/react-inputs';
import { DetailLabel } from '@strata/react-label-value-pairs';
import { SuggestMultiSelectFormik } from '@strata/react-selects';
import { Tag } from '@strata/react-tags';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { FieldArray, Formik } from 'formik';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { putStateList } from 'api/auctionsAPI';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetStateListsQuery } from 'store/api/auctionApi';

export const StateListDetails = () => {
    const { stateListId } = useParams() as { stateListId: string };
    const navigate = useNavigate();
    const userCanEdit = usePermissionCheck(Permissions.WRITE_AUCTIONS);
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);

    const isCreate = stateListId === 'create';

    const { data: stateLists, isFetching, refetch } = useGetStateListsQuery();
    const stateList = stateLists?.find((list) => list.stateListId === stateListId);

    useEffect(() => {
        if (!(isCreate || isFetching || stateList)) {
            handleError('State list not found.');
            navigate(AppRoutes.stateLists);
        }
    }, [isCreate, isFetching, stateList, navigate]);

    const validationSchema = Yup.object({
        listName: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            // Check for unique name
            .test({
                test: (currentVal, { path, createError }) =>
                    stateLists?.some(
                        (list) => list.stateListId !== stateListId && list.listName === currentVal,
                    )
                        ? createError({
                              path,
                              message: `"${currentVal}" is used by another State List.`,
                          })
                        : true,
            }),
        states: Yup.array(Yup.string().oneOf(Object.values(USStates)).required())
            .required(validationStrings.requiredField.errorMessage)
            .min(1, validationStrings.requiredField.errorMessage),
        statesToAdd: Yup.array(
            Yup.object({ secondaryData: Yup.string().oneOf(Object.values(USStates)).required() }),
        ).required(validationStrings.requiredField.errorMessage),
    });

    return (
        <FormPageContainer>
            <FullscreenLoading
                isShowing={!isCreate && isFetching}
                loadingText="Loading state lists."
            />
            <div
                className="flex__gap--medium padding__around--medium"
                style={{ flexDirection: 'column', height: '100%' }}
            >
                <ActionButtonPageTitle
                    label="State Lists"
                    title="State List Details"
                    id="state-list-details-page-title-button"
                    onClick={() => navigate(AppRoutes.stateLists)}
                />
                <Formik
                    initialValues={
                        {
                            listName: stateList?.listName,
                            states: stateList?.states,
                            statesToAdd: [],
                        } as Yup.InferType<typeof validationSchema>
                    }
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={async (values) => {
                        try {
                            await putStateList({
                                npi: selectedNpi!,
                                stateListId: !isCreate ? stateListId : undefined,
                                ...values,
                            });
                            SuccessToast({
                                message: `State list successfully ${
                                    isCreate ? 'created' : 'updated'
                                }.`,
                            });
                            await refetch();
                            navigate(AppRoutes.stateLists);
                        } catch (err) {
                            handleError(`Error saving ${values.listName}.`, err as Error);
                        }
                    }}
                >
                    {(formik) => (
                        <div style={{ overflow: 'auto', flexGrow: 1 }}>
                            <FormContainer
                                formTitle="State List Configuration"
                                onFormSubmit={formik.handleSubmit}
                                onCancelClick={() => navigate(AppRoutes.stateLists)}
                                isSubmitting={
                                    !(formik.dirty && formik.isValid) || formik.isSubmitting
                                }
                                formLeftContent={
                                    <>
                                        <TextInputFormik
                                            id="listName"
                                            label="State List Name"
                                            helperText="Will be used as the display name throughout this application when referring to this state list."
                                            required
                                            disabled={!userCanEdit}
                                        />
                                        <div
                                            className="flex__gap--small"
                                            style={{ maxWidth: 600, alignItems: 'flex-start' }}
                                        >
                                            <div style={{ flexGrow: 1 }}>
                                                <SuggestMultiSelectFormik
                                                    id="statesToAdd"
                                                    label="State(s)"
                                                    required
                                                    disabled={!userCanEdit}
                                                    values={USStateOptions.filter(
                                                        ({ secondaryData }) =>
                                                            !formik.values.states?.includes(
                                                                secondaryData,
                                                            ),
                                                    )}
                                                />
                                            </div>
                                            <div style={{ paddingTop: 20.5, flexShrink: 0 }}>
                                                <Button
                                                    disabled={
                                                        !userCanEdit ||
                                                        formik.values.statesToAdd.length < 1
                                                    }
                                                    intent={Button.Intent.PRIMARY}
                                                    label={`Add State${
                                                        formik.values.statesToAdd.length > 1
                                                            ? 's'
                                                            : ''
                                                    }`}
                                                    id="add-states-button"
                                                    onClick={() => {
                                                        void formik.setValues({
                                                            ...formik.values,
                                                            statesToAdd: [],
                                                            states: [
                                                                ...(formik.values.states ?? []),
                                                                ...formik.values.statesToAdd.map(
                                                                    ({ secondaryData }) =>
                                                                        secondaryData,
                                                                ),
                                                            ].sort(sortAsc),
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <DetailLabel label="Selected States" />
                                        <FieldArray name="states">
                                            {(arrayHelpers) => (
                                                <div style={{ width: 170 }}>
                                                    {formik.values.states?.map((state, i) => (
                                                        <Tag
                                                            key={state}
                                                            className="margin__bottom--small margin__right--medium"
                                                            label={`${USStateNames[state]} - ${state}`}
                                                            onRemove={
                                                                userCanEdit
                                                                    ? () => arrayHelpers.remove(i)
                                                                    : undefined
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </FieldArray>
                                    </>
                                }
                            />
                        </div>
                    )}
                </Formik>
            </div>
        </FormPageContainer>
    );
};
